import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { portalLogin, getPortalProfile } from '../services/portalApi';

interface PortalCustomer {
  id: number;
  name: string;
  email: string;
  contact_name?: string;
  must_change_password: boolean;
  last_login_at: string | null;
  org_logo_url?: string | null;
  org_name?: string | null;
}

interface PortalAuthState {
  customer: PortalCustomer | null;
  orgAlias: string;
  loading: boolean;
  login: (email: string, password: string, orgAlias: string) => Promise<{ must_change_password: boolean }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthState | undefined>(undefined);

export const usePortalAuth = () => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used inside PortalAuthProvider');
  return ctx;
};

export const PortalAuthProvider: React.FC<{ children: ReactNode; orgAlias: string }> = ({
  children,
  orgAlias,
}) => {
  const [customer, setCustomer] = useState<PortalCustomer | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async () => {
    const token = localStorage.getItem('portal_access_token');
    if (!token) { setLoading(false); return; }
    try {
      const data = await getPortalProfile();
      setCustomer(data);
    } catch {
      localStorage.removeItem('portal_access_token');
      localStorage.removeItem('portal_refresh_token');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  const login = async (email: string, password: string, alias: string) => {
    const data = await portalLogin(email, password, alias);
    localStorage.setItem('portal_access_token', data.access_token);
    localStorage.setItem('portal_refresh_token', data.refresh_token);
    setCustomer(data.customer);
    return { must_change_password: data.must_change_password };
  };

  const logout = () => {
    localStorage.removeItem('portal_access_token');
    localStorage.removeItem('portal_refresh_token');
    setCustomer(null);
  };

  return (
    <PortalAuthContext.Provider value={{ customer, orgAlias, loading, login, logout, refreshProfile: loadProfile }}>
      {children}
    </PortalAuthContext.Provider>
  );
};
